import { mediacoreApi } from '@apis/mediacoreApi'
import { deleteByIdMutation, getAllQuery, getByIdAndClientIdQuery, updateByIdMutation } from '@apis/mediacoreQueries'
import { Notification, NotificationSetting } from '@types'

const notificationsApi = mediacoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationsCount: builder.query<{ client: number; count: number }[], void>({
      query: getAllQuery('core/notificaciones/count'),
      providesTags: ['NotificationsCount'],
    }),
    getNotificationById: builder.query<Notification, { id: number; clientId: number }>({
      query: getByIdAndClientIdQuery('core/notificaciones'),
      providesTags: (_result, _error, { id }) => [{ type: 'Notification', id }],
    }),
    updateNotification: builder.mutation<Notification, { id: number; data: Partial<Notification> }>({
      query: updateByIdMutation('core/notificaciones'),
      invalidatesTags: ['NotificationTable', 'NotificationsCount'],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedNotification } = await queryFulfilled
          dispatch(
            notificationsApi.util.updateQueryData(
              'getNotificationById',
              { id, clientId: updatedNotification.client },
              (draft) => {
                Object.assign(draft, updatedNotification)
              },
            ),
          )
        } catch {}
      },
    }),
    deleteNotifications: builder.mutation<void, { ids: number[] }>({
      query: ({ ids }) => ({
        url: `core/notificaciones/delete_multiple/`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['NotificationsCount', 'NotificationTable'],
    }),
    deleteNotification: builder.mutation<Notification, { id: number; clientId: number }>({
      query: deleteByIdMutation('core/notificaciones'),
      invalidatesTags: ['NotificationTable'],
    }),
    getNotificationsSettings: builder.query<NotificationSetting[], { user: number; clientId: number }>({
      query: ({ clientId: client, user }: { clientId: number; user: number }) => ({
        url: `core/notificaciones-configuracion/`,
        method: 'GET',
        params: { format: 'json', client, user },
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'NotificationSetting' as const, id })), 'NotificationSetting']
          : ['NotificationSetting'],
    }),
    getNotificationSettingById: builder.query<NotificationSetting, { id: number; clientId: number }>({
      query: getByIdAndClientIdQuery('core/notificaciones-configuracion'),
      providesTags: (_result, _error, { id }) => [{ type: 'NotificationSetting', id }],
    }),
    updateNotificationSettings: builder.mutation<
      NotificationSetting,
      { id: number; client: number; user: number; data: NotificationSetting['data'] }
    >({
      query: ({
        id,
        data,
        client,
        user,
      }: {
        id: number
        data: NotificationSetting['data']
        client: number
        user: number
      }) => ({
        url: `core/notificaciones-configuracion/${id}/`,
        method: 'PATCH',
        body: { id, data, client, user },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedNotification } = await queryFulfilled
          dispatch(
            notificationsApi.util.updateQueryData(
              'getNotificationSettingById',
              { id, clientId: updatedNotification.client },
              (draft) => {
                Object.assign(draft, updatedNotification)
              },
            ),
          )
          dispatch(
            notificationsApi.util.updateQueryData(
              'getNotificationsSettings',
              { user: updatedNotification.user, clientId: updatedNotification.client },
              (draft) => {
                return draft.map((notification) => (notification.id !== id ? notification : updatedNotification))
              },
            ),
          )
        } catch {}
      },
    }),
    createNotificationSetting: builder.mutation<
      NotificationSetting,
      { user: number; client: number; data: Partial<NotificationSetting['data']> }
    >({
      query: ({ user, client, data }: { user: number; client: number; data: NotificationSetting['data'] }) => ({
        url: `core/notificaciones-configuracion/`,
        method: 'POST',
        body: { user, client, data },
      }),
      invalidatesTags: ['User'],
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          const { data: createdNotificationSetting } = await queryFulfilled
          dispatch(
            notificationsApi.util.updateQueryData(
              'getNotificationsSettings',
              { clientId: createdNotificationSetting.client, user: createdNotificationSetting.user },
              (draft) => {
                draft.unshift(createdNotificationSetting)
                return draft
              },
            ),
          )
        } catch {}
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetNotificationsCountQuery,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
  useDeleteNotificationsMutation,
  useUpdateNotificationSettingsMutation,
  useGetNotificationsSettingsQuery,
  useCreateNotificationSettingMutation,
} = notificationsApi
